<template>
  <div :class="classModifiers">
    <account-form-loader v-if="!isComponentReady" theme="deposit"></account-form-loader>

    <account-upload-document
      v-else-if="documentUploadInfoVisible"
      :source="documentUploadSource"
      :isPending="documentUploadInfoPending"
      :theme="accountUploadDocumentTheme"
      type="card"
    ></account-upload-document>

    <account-ssn-info-upload
      v-else-if="collectSsnInfo"
      :isPopup="isPopup"
      :theme="accountUploadDocumentTheme"
    ></account-ssn-info-upload>

    <template v-else>
      <form v-if="isStep('INITIAL')" @submit.prevent="onConfirmDeposit">
        <alert-box class="add-funds-credit-card__alert-box" level="warning" v-if="errorMessage">
          <div v-html="errorMessage"></div>
        </alert-box>

        <alert-box class="add-funds-credit-card__alert-box" level="warning" v-if="avsMessage">
          Please provide a billing address.
        </alert-box>

        <fee-waived-banner
          class="add-funds-credit-card__alert-box"
          level="success"
          v-if="showPromoNotification && false"
          :text="promoCodeDetail.friendlyDescription"
        >
        </fee-waived-banner>

        <div v-if="depositWarning" v-html="depositWarning" class="predictit-embedded-alert"></div>

        <fee-waived-banner
          :level="instantCreditBoxLevel"
          :text="instantCreditBoxMessage"
          v-if="showInstantCreditNotification && false"
        >
        </fee-waived-banner>

        <template v-if="!errorMessage">
          <div class="add-funds-credit-card__amount-section">
            <div
              class="add-funds-credit-card__section-label add-funds-credit-card__section-label--center"
            >
              Deposit
            </div>
            <deposit-amount-picker
              v-model="fields.amount"
              :deposit-amounts="depositAmounts"
              :has-error="amountHasError"
              :error-message="amountValidationMessage"
              :disabled="isFormDisabled"
              @add="onUpdateAmount"
              @blur="handleAmountBlur"
              @input="$v.fields.amount.$touch"
            ></deposit-amount-picker>
          </div>

          <fee-status-message
            class="add-funds-credit-card__no-fee-message"
            :amount="fields.amount"
            :fee-percent="processingFeePercent"
            :fee-free-cap="feeFreeCap"
            :fee-discount-eligibility="feeDiscountEligibility"
            :fee-cap="feeCap"
            :prefix="noFeeMessagePrefix"
          ></fee-status-message>

          <div class="add-funds-credit-card__section-label">Card Details</div>

          <div class="add-funds-credit-card__card-fields">
            <div class="add-funds-credit-card__field-row">
              <form-input
                v-model="fields.name"
                :tabindex="isWrapperApp ? 0 : 2"
                @input="$v.fields.name.$touch"
                :disabled="isFormDisabled"
                :validation="$v.fields.name"
                :name="fieldName('name')"
                autocomplete="name"
                type="text"
                :required="true"
                gtm="Deposit,Credit Card Name"
                placeholder="Name on Card"
              ></form-input>
            </div>

            <div class="add-funds-credit-card__field-row">
              <form-input
                v-model="fields.number"
                theme="card-type"
                :tabindex="isWrapperApp ? 0 : 4"
                ref="cnumber"
                @input="$v.fields.number.$touch"
                :disabled="isFormDisabled"
                :validation="$v.fields.number"
                :name="fieldName('number')"
                autocomplete="off"
                type="text"
                gtm="Deposit,Credit Card Number"
                :hideIconOnValue="false"
                :required="true"
                placeholder="Card Number"
              >
                <template v-slot:icon>
                  <img :src="cardTypeImage" class="add-funds-credit-card__card-type-img" />
                </template>
              </form-input>
              <div
                v-if="cardTypeHasError"
                class="add-funds-credit-card__error add-funds-credit-card__error--no-margin-bottom"
              >
                {{ cardTypeValidationMessage }}
              </div>
            </div>

            <div class="add-funds-credit-card__expiry-row">
              <div class="add-funds-credit-card__expiry-month">
                <form-select
                  v-model="fields.expiryDateMonth"
                  :searchable="isLayoutDesktop"
                  :autofill-assist="true"
                  :tabindex="isWrapperApp ? 0 : 5"
                  @select="(e) => tabTo(e, 'year')"
                  @input="$v.fields.expiryDateMonth.$touch"
                  :disabled="isFormDisabled"
                  :validation="$v.fields.expiryDateMonth"
                  placeholder="MM"
                  :options="months"
                  track-by="value"
                  label="label"
                  gtm="Deposit,Credit Card Expiry Month"
                  :required="true"
                ></form-select>
              </div>
              <div class="add-funds-credit-card__expiry-year">
                <form-select
                  v-model="fields.expiryDateYear"
                  :searchable="isLayoutDesktop"
                  :autofill-assist="true"
                  :tabindex="isWrapperApp ? 0 : 6"
                  ref="year"
                  @select="(e) => tabTo(e, 'cvv')"
                  @input="$v.fields.expiryDateYear.$touch"
                  :disabled="isFormDisabled"
                  :validation="$v.fields.expiryDateYear"
                  placeholder="YYYY"
                  :options="years"
                  track-by="value"
                  label="label"
                  gtm="Deposit,Credit Card Expiry Year"
                  :required="true"
                ></form-select>
              </div>
              <div class="add-funds-credit-card__cvv">
                <form-input
                  v-model="fields.cvv"
                  :tabindex="isWrapperApp ? 0 : 7"
                  ref="cvv"
                  @input="$v.fields.cvv.$touch"
                  :disabled="isFormDisabled"
                  :validation="$v.fields.cvv"
                  :name="fieldName('cvv')"
                  autocomplete="off"
                  type="text"
                  :required="true"
                  gtm="Deposit,Security Code"
                  placeholder="CVV"
                  :hideIconOnValue="false"
                >
                  <template v-slot:icon>
                    <notification-icon
                      @showPopup="showCVVModal"
                      :reversed="true"
                    ></notification-icon>
                  </template>
                </form-input>
              </div>
            </div>
          </div>

          <template v-if="avsFlag">
            <div
              class="add-funds-credit-card__section-label add-funds-credit-card__section-label--margin-top"
            >
              Billing Address
            </div>
            <div class="add-funds-credit-card__card-fields">
              <div class="add-funds-credit-card__field-row">
                <form-input
                  v-model="fields.address"
                  @input="$v.fields.address.$touch"
                  :validation="$v.fields.address"
                  :disabled="isFormDisabled"
                  name="street"
                  type="text"
                  :required="true"
                  autocomplete="street-address"
                  placeholder="Street Address"
                ></form-input>
              </div>
              <div class="add-funds-credit-card__avs-row">
                <div class="add-funds-credit-card__avs-city">
                  <form-input
                    v-model="fields.city"
                    @input="$v.fields.city.$touch"
                    :validation="$v.fields.city"
                    :disabled="isFormDisabled"
                    name="city"
                    type="text"
                    :required="true"
                    autocomplete="address-line1"
                    placeholder="City"
                  ></form-input>
                </div>
                <div class="add-funds-credit-card__avs-zip">
                  <form-input
                    v-model="fields.zipCode"
                    @input="$v.fields.zipCode.$touch"
                    :validation="$v.fields.zipCode"
                    :disabled="isFormDisabled"
                    name="zipCode"
                    type="text"
                    :required="true"
                    autocomplete="postal-code"
                    placeholder="Zip/Postal Code"
                  ></form-input>
                </div>
              </div>
            </div>
          </template>
          <div class="add-funds-credit-card__line"></div>
          <template v-if="isDepositPromoInputBoxEnabled">
            <div class="add-funds-credit-card__promo-section">
              <promo-code-input
                :is-initial="promoInit"
                :is-loading="promoApplying"
                :is-applied="promoApplied"
                :has-error="promoHasError"
                :applied-code="promoAppliedCode"
                :applied-amount="promoBonus"
                @initialised="onInitialisedPromoCode"
                @apply="onApplyPromoCode"
                @remove="onRemovePromoCode"
              ></promo-code-input>
            </div>
            <div class="add-funds-credit-card__line"></div>
          </template>

          <div class="add-funds-credit-card__submit">
            <form-submit label="Review and Confirm" :disabled="isConfirmDisabled"></form-submit>
          </div>
        </template>
      </form>

      <div v-if="isStep('CONFIRM')" class="add-funds-credit-card__confirm">
        <div class="add-funds-credit-card__back" @click.prevent="onBack">
          <span class="add-funds-credit-card__back-arrow"></span>
          <span class="add-funds-credit-card__back-label">BACK</span>
        </div>
        <deposit-amount-overview :amount="fields.amount">
          <template v-slot:title>Deposit</template>
          <template v-slot:subtitle>{{ cardMasked }}</template>
        </deposit-amount-overview>
        <br /><br />
        <deposit-info-overview
          class="add-funds-credit-card__overview"
          :card-masked="cardMasked"
          :amount="fields.amount"
          :fee-percent="processingFeePercent"
          :fee-amount="processingFeeAmount"
          :was-fee-discount-applied="wasFeeDiscountApplied"
          :is-fee-waived="isFeeWaived || wasFeeDiscountApplied"
          :fee-free-amount="feeFreeAmount"
          :fee-cap="feeCap"
          :separate-credit-code="promoAppliedCode"
          :separate-credit-amount="promoApplied ? promoBonus : 0"
          :promotional-credit-amount="promotionalCreditAmount"
          :total-to-wallet="totalToWallet"
          :arrives-text="timeframeMsg || 'Instant'"
        ></deposit-info-overview>

        <div class="add-funds-credit-card__confirm-actions">
          <button
            @click.prevent="onSubmitDeposit"
            :disabled="isConfirmDisabled"
            class="button button--large button--primary button--full-width"
          >
            Confirm Deposit
          </button>
          <button
            @click.prevent="onBack"
            class="button button--large button--inverted button--full-width"
          >
            Edit Amount
          </button>
        </div>
      </div>

      <deposit-result v-if="isStep('TRY_AGAIN')">
        <template v-slot:heading> Deposit Failed </template>
        <template v-slot:icon>
          <img src="@/assets/icons/wallet/deposit-fail.svg" alt="Payment failed" />
        </template>
        <template v-slot:body>
          <div class="add-funds-credit-card__fail-subtitle">We couldn’t complete your deposit</div>
          <p
            v-if="errorMessageDepositFail"
            v-html="errorMessageDepositFail"
            class="add-funds-credit-card__fail-message"
          ></p>
          <p v-else class="add-funds-credit-card__fail-message">
            No money has been released.<br />
            Your card wasn't charged.
          </p>
        </template>
        <template v-slot:actions>
          <button
            @click.prevent="onBack"
            class="button button--large button--primary button--full-width"
          >
            Try Again
          </button>
          <button
            @click.prevent="onUseDifferentCard"
            class="button button--large button--full-width button--inverted"
          >
            Use a Different Card
          </button>
        </template>
      </deposit-result>

      <deposit-result v-if="isStep('SUCCESS')">
        <template v-slot:heading> Deposit Successful </template>
        <template v-slot:icon>
          <img src="@/assets/icons/wallet/deposit-success.svg" alt="Deposit successful" />
        </template>
        <template v-slot:body>
          <div class="add-funds-credit-card__success-amount">{{ successAmount }}</div>
          <div class="add-funds-credit-card__success-subtitle">Added to your wallet</div>
          <div class="add-funds-credit-card__success-note">Your funds are available now.</div>
          <deposit-info-overview
            class="add-funds-credit-card__overview add-funds-credit-card__overview--full-margin"
            :card-masked="cardMasked"
            :amount="fields.amount"
            :fee-percent="processingFeePercent"
            :fee-amount="processingFeeAmount"
            :was-fee-discount-applied="wasFeeDiscountApplied"
            :is-fee-waived="isFeeWaived || wasFeeDiscountApplied"
            :fee-free-amount="feeFreeAmount"
            :fee-cap="feeCap"
            :separate-credit-code="promoAppliedCode"
            :separate-credit-amount="promoApplied ? promoBonus : 0"
            :promotional-credit-amount="promotionalCreditAmount"
            :total-to-wallet="totalToWallet"
            :arrives-text="timeframeMsg || 'Instant'"
          ></deposit-info-overview>
        </template>
        <template v-slot:actions>
          <deposit-done-links :show-pending-link="false" @done="onSuccessDone"></deposit-done-links>
        </template>
      </deposit-result>

      <deposit-disclaimer
        :disclaimer="disclaimer"
        v-if="!errorMessage && !documentUploadInfoVisible"
      ></deposit-disclaimer>
    </template>
  </div>
</template>

<script src="./add-funds-credit-card.js"></script>
<style src="./add-funds-credit-card.scss" lang="scss"></style>
